import { PanelError, bodyOf, httpStatusOf, isOutcomeUnknown, toPanelError } from '@/console/api/errors'
import {
  ReturnOriginRemapChange,
  ReturnOriginRemapOutcome,
  ReturnOriginRemapRejection,
  ReturnOriginRemapRejectionCode,
  ReturnOriginRemapRequest,
  ReturnOriginRemapResponse,
} from '@/console/api/types'

/**
 * 입고 `반품 원주문 재매핑` 패널의 순수 로직. 계산은 전부 여기 두고 `OriginRemapPanel` 은 결과만 그린다.
 * 입력 검증은 서버 400 규칙(docs/ONCALL_RETURN_ORIGIN_REMAP_API.md §3.1)과 같게 맞춰 화면에서 먼저 끊는다.
 */
/** A·B·Y 를 찾는 SQL 이 실린 절. 원본 런북은 moms-be 저장소에 있어 콘솔에서 바로 열 수 없어 이 가이드로 보낸다. */
export const ORIGIN_REMAP_GUIDE_QUERIES_URL =
  'https://github.com/musinsa/moms-fe/blob/main/docs/ONCALL_RETURN_ORIGIN_REMAP_GUIDE.md#22-aby-를-찾는-조회'

export const MAX_REF_NO_LENGTH = 100
export const MAX_REASON_LENGTH = 500

const REF_NO_PATTERN = /^[0-9A-Za-z_-]+$/
// BE 가 막는 줄 구분자(U+0085·U+2028·U+2029)까지 막는다 — reason 은 서버 요약 로그 한 줄에 그대로 실린다.
// eslint-disable-next-line no-control-regex -- 제어문자 존재 자체를 검사하는 게 목적이다.
const CONTROL_CHARACTER_PATTERN = /[\x00-\x1F\x7F\x85\u2028\u2029]/

/** 화면 입력 원문. 숫자 필드도 문자열로 들고 있어야 "한 글자라도 바뀌면 다시 잠근다" 를 원문 비교로 판정할 수 있다. */
export type OriginRemapFormValues = {
  misMappedInboundReportId: string
  blockedInboundReportId: string
  correctRefNo1: string
  correctRefNo2: string
  reason: string
}

export type OriginRemapField = keyof OriginRemapFormValues

export const ORIGIN_REMAP_FIELDS: OriginRemapField[] = [
  'misMappedInboundReportId',
  'blockedInboundReportId',
  'correctRefNo1',
  'correctRefNo2',
  'reason',
]

export const EMPTY_ORIGIN_REMAP_FORM: OriginRemapFormValues = {
  misMappedInboundReportId: '',
  blockedInboundReportId: '',
  correctRefNo1: '',
  correctRefNo2: '',
  reason: '',
}

/** dryRun 여부는 판정·실행 버튼이 정한다. */
export type ReturnOriginRemapInput = Omit<ReturnOriginRemapRequest, 'dryRun'>

export type OriginRemapFieldErrors = Partial<Record<OriginRemapField, string>>

export type OriginRemapValidation = { input: ReturnOriginRemapInput; errors: null } | { input: null; errors: OriginRemapFieldErrors }

/** `UI_GRREPORT_DETAIL.id` 는 auto-increment PK 라 양의 안전 정수만 유효하다. 아니면 `null`. */
export const parseInboundReportId = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) {
    return null
  }

  const id = Number(trimmed)
  return id > 0 && Number.isSafeInteger(id) ? id : null
}

const validateInboundReportId = (raw: string): string | null => {
  if (raw.trim().length === 0) {
    return '입고실적 id 는 필수예요.'
  }

  return parseInboundReportId(raw) === null ? '입고실적 id 는 양의 정수만 넣을 수 있어요.' : null
}

const validateBlockedInboundReportId = (values: OriginRemapFormValues): string | null => {
  const formatError = validateInboundReportId(values.blockedInboundReportId)
  if (formatError) {
    return formatError
  }

  const isSameAsMisMapped = parseInboundReportId(values.misMappedInboundReportId) === parseInboundReportId(values.blockedInboundReportId)
  return isSameAsMisMapped ? 'B 는 A 와 다른 입고실적이어야 해요.' : null
}

export const validateRefNo = (refNo: string): string | null => {
  if (refNo.trim().length === 0) {
    return '원주문 번호는 필수예요.'
  }

  if (refNo.length > MAX_REF_NO_LENGTH) {
    return `원주문 번호는 ${MAX_REF_NO_LENGTH}자를 넘을 수 없어요.`
  }

  return REF_NO_PATTERN.test(refNo) ? null : '원주문 번호는 영문·숫자·_·- 만 쓸 수 있어요(공백·따옴표·한글 불가).'
}

export const validateReason = (reason: string): string | null => {
  if (reason.trim().length === 0) {
    return '사유는 필수예요.'
  }

  if (reason.length > MAX_REASON_LENGTH) {
    return `사유는 ${MAX_REASON_LENGTH}자를 넘을 수 없어요.`
  }

  return CONTROL_CHARACTER_PATTERN.test(reason) ? '사유에 줄바꿈·탭 같은 제어문자를 넣을 수 없어요.' : null
}

const collectFieldErrors = (values: OriginRemapFormValues): OriginRemapFieldErrors => {
  const errorByField: Record<OriginRemapField, string | null> = {
    misMappedInboundReportId: validateInboundReportId(values.misMappedInboundReportId),
    blockedInboundReportId: validateBlockedInboundReportId(values),
    correctRefNo1: validateRefNo(values.correctRefNo1),
    correctRefNo2: validateRefNo(values.correctRefNo2),
    reason: validateReason(values.reason),
  }

  return Object.fromEntries(Object.entries(errorByField).filter(([, error]) => error !== null)) as OriginRemapFieldErrors
}

/** 문제가 없으면 요청 본문(dryRun 제외)을, 있으면 필드별 사유를 돌려준다. 문자열 필드는 가공하지 않는다. */
export const validateOriginRemapForm = (values: OriginRemapFormValues): OriginRemapValidation => {
  const errors = collectFieldErrors(values)
  if (Object.keys(errors).length > 0) {
    return { input: null, errors }
  }

  return {
    input: {
      misMappedInboundReportId: Number(values.misMappedInboundReportId.trim()),
      blockedInboundReportId: Number(values.blockedInboundReportId.trim()),
      correctRefNo1: values.correctRefNo1,
      correctRefNo2: values.correctRefNo2,
      reason: values.reason,
    },
    errors: null,
  }
}

/** 실행은 판정을 통과한 입력 그대로일 때만 열린다. 원문을 필드별로 비교해 공백 한 칸 차이도 다른 입력으로 본다. */
export const isSameAsDryRunInput = (dryRunValues: OriginRemapFormValues | null, currentValues: OriginRemapFormValues): boolean =>
  dryRunValues !== null && ORIGIN_REMAP_FIELDS.every((field) => dryRunValues[field] === currentValues[field])

/** 판정을 통과한 입력 원문과 그때 받은 계획. 실행 잠금 해제와 확인 다이얼로그가 이 값만 본다. */
export type DryRunPass = { values: OriginRemapFormValues; changes: ReturnOriginRemapChange[] }

export type OriginRemapGate = {
  input: ReturnOriginRemapInput | null
  errors: OriginRemapFieldErrors | null
  canDryRun: boolean
  canExecute: boolean
  /** 판정은 통과했지만 그 뒤 입력이 바뀌어 실행을 잠근 상태. */
  isDryRunStale: boolean
}

export const deriveOriginRemapGate = (values: OriginRemapFormValues, dryRunPass: DryRunPass | null, submitting: boolean): OriginRemapGate => {
  const { input, errors } = validateOriginRemapForm(values)
  const isDryRunCurrent = isSameAsDryRunInput(dryRunPass?.values ?? null, values)
  const canDryRun = input !== null && !submitting

  return { input, errors, canDryRun, canExecute: canDryRun && isDryRunCurrent, isDryRunStale: dryRunPass !== null && !isDryRunCurrent }
}

export const ORIGIN_REMAP_OUTCOME = {
  DRY_RUN_PASSED: 'DRY_RUN_PASSED',
  APPLIED: 'APPLIED',
  REJECTED: 'REJECTED',
} as const satisfies Record<ReturnOriginRemapOutcome, ReturnOriginRemapOutcome>

export const ORIGIN_REMAP_RESULT_KIND = {
  PLANNED: 'planned',
  APPLIED: 'applied',
  REJECTED: 'rejected',
  /** 실행 요청에 판정 결과가 왔다. dryRun 이 잘못 실려 나간 버그 상태다. */
  DRY_RUN_RETURNED_ON_EXECUTE: 'dryRunReturnedOnExecute',
} as const

export type OriginRemapResultKind = (typeof ORIGIN_REMAP_RESULT_KIND)[keyof typeof ORIGIN_REMAP_RESULT_KIND]

export const classifyOriginRemapResult = (response: ReturnOriginRemapResponse, dryRun: boolean): OriginRemapResultKind => {
  if (response.outcome === ORIGIN_REMAP_OUTCOME.APPLIED) {
    return ORIGIN_REMAP_RESULT_KIND.APPLIED
  }

  if (response.outcome === ORIGIN_REMAP_OUTCOME.REJECTED) {
    return ORIGIN_REMAP_RESULT_KIND.REJECTED
  }

  return dryRun ? ORIGIN_REMAP_RESULT_KIND.PLANNED : ORIGIN_REMAP_RESULT_KIND.DRY_RUN_RETURNED_ON_EXECUTE
}

/** 거부 코드별 다음 행동 안내. 화면은 이 문구 뒤에 서버 `detail` 원문을 그대로 붙인다. */
export const REJECTION_GUIDANCE = {
  INVALID_MIS_MAPPED_REPORT:
    'A(오매핑 입고실적)가 없거나, 반품이 아니거나, INOOINT 가 아니거나, 과입고·입고취소거나, 실적값이 없어요. A id 를 다시 확인하세요. 다른 화주면 이 조치 대상이 아니에요.',
  INVALID_BLOCKED_REPORT:
    'B(막힌 입고실적)가 잘못됐거나 화주·상품·원주문 라인이 A 와 달라요. A·B 를 다시 확인하세요. 이미 실행돼 A 가 Y 로 옮겨졌을 수 있어요.',
  NOT_SAME_COMBINED_SHIPMENT:
    'Y 가 X 와 같거나, 두 라인이 같은 합포 송장·같은 상품이 아니거나, Y 원출고의 판매채널이 비어 있어요. Y 를 다시 찾으세요.',
  CLAIM_ID_CONVENTION_MISMATCH: 'A 또는 B 의 claim_id 가 원주문 일련번호 관례를 벗어났어요. 이 조치로 고치지 않아요.',
  INBOUND_PLAN_NOT_UNIQUE: 'A 의 입고예정이 정확히 1행이 아니에요. OPR_AN_STATUS 를 조회해 원인을 보세요.',
  SOURCE_ERP_ROW_MISMATCH:
    'X 라인 ERP 행의 개수·수량·날짜가 맞지 않거나, 아직 SAP 로 나가지 않았어요(API_FLAG=0). 전송 대기가 아니면 ERP 담당과 협의하세요.',
  TARGET_LINE_NOT_REMAPPABLE: 'Y 라인의 남은 수량이 부족하거나 이미 반영됐어요. Y 라인의 ERP 행을 보세요.',
  ERP_ROW_NOT_CREATED: '보정 뒤 ERP 행이 생기지 않아 전부 롤백했어요. 서버 warn 로그로 원인을 확인한 뒤 판정(dryRun)부터 다시 하세요.',
} satisfies Record<ReturnOriginRemapRejectionCode, string>

/** 다시 찾을 값을 가이드의 어느 조회로 구하는지. 화면은 안내 문구 뒤에 이 절로 가는 링크를 붙인다. */
export const REJECTION_QUERY_SECTION: Partial<Record<ReturnOriginRemapRejectionCode, string>> = {
  NOT_SAME_COMBINED_SHIPMENT: '§2.2 ④',
  TARGET_LINE_NOT_REMAPPABLE: '§2.2 ⑤',
}

const SOURCE_ERP_ROW_MISMATCH: ReturnOriginRemapRejectionCode = 'SOURCE_ERP_ROW_MISMATCH'
export const API_FLAG_PENDING_MARKER = 'API_FLAG=0'
export const TRANSFER_BATCH_RETRY_GUIDANCE =
  'X 라인 ERP 행이 아직 전송 대기(API_FLAG=0)예요. WebSI 전송 배치가 한 주기 돈 뒤에는 같은 요청이 유효해요.'

/** 이 거부만 같은 요청을 한 주기 뒤 다시 보낼 수 있다(§3.4 `△`). */
export const isWaitingForTransferBatch = (rejection: ReturnOriginRemapRejection): boolean =>
  rejection.code === SOURCE_ERP_ROW_MISMATCH && rejection.detail.includes(API_FLAG_PENDING_MARKER)

/** before/after 는 테이블마다 키가 달라 표 열로 펼치지 않고 `key=value` 로 이어 적는다. */
export const formatChangeValues = (values: Record<string, string | number | null>): string =>
  Object.entries(values)
    .map(([key, value]) => `${key}=${value ?? 'null'}`)
    .join(' · ')

/** 같은 Y 라인에 다른 반품이 거의 동시에 적재되는 창은 서버 가드가 못 막는다 — 이 확인이 유일한 수단이라 생략할 수 없다. */
export const POST_APPLY_CHECKLIST = [
  "WebSI 전송 배치가 한 주기 돈 뒤, Y 라인에 새로 만든 ERP 반품행이 API_FLAG=1 · PISTAT='S' 인지 확인하세요.",
  '두 주문(X · Y)의 라인별 출고 수량과 반품 수량이 같은지 확인하세요.',
  '응답 JSON(requestId · changes before/after)을 티켓에 붙여 되돌리기 근거로 남기세요.',
]

export const ORIGIN_REMAP_FORBIDDEN_MESSAGE = '이 조치에는 MOMS_ENG 권한이 필요해요.'
export const NO_RESPONSE_MESSAGE = '응답을 받지 못했어요. 다시 실행하지 말고 입고실적 상태를 먼저 확인하세요.'
export const EXECUTE_SERVER_ERROR_FALLBACK_MESSAGE = '요청이 실패했어요. 다시 실행하지 말고 입고실적 상태를 먼저 확인하세요.'

const HTTP_SERVER_ERROR_MIN_STATUS = 500

const isServerError = (error: unknown): boolean => (httpStatusOf(error) ?? 0) >= HTTP_SERVER_ERROR_MIN_STATUS

const serverMessageOf = (error: unknown): string | undefined =>
  (bodyOf(error) as { meta?: { message?: string } } | undefined)?.meta?.message

/**
 * 실행이 응답 없이 끊겼거나 504 면 서버가 커밋했는지 알 수 없다. 그 밖의 5xx 는 롤백된 상태라(§3.5) 서버 사유를 보여준다.
 * 판정은 데이터를 바꾸지 않아 콘솔 공통 규칙을 그대로 따른다.
 */
export const toOriginRemapError = (error: unknown, dryRun: boolean): PanelError => {
  if (!dryRun && isOutcomeUnknown(error)) {
    return { kind: 'other', message: NO_RESPONSE_MESSAGE }
  }

  if (!dryRun && isServerError(error)) {
    return { kind: 'other', message: serverMessageOf(error) ?? EXECUTE_SERVER_ERROR_FALLBACK_MESSAGE }
  }

  return toPanelError(error, ORIGIN_REMAP_FORBIDDEN_MESSAGE)
}
